import React, { useEffect, useRef, useState } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useCartLiveValidationActions } from 'features/cart/hooks/use-cart-live-validation-actions';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import { useEnableLoyaltyServiceRewards } from 'features/reward/hooks/use-enable-loyalty-service-rewards';
import {
  useEnableServiceRewardsBrowse,
  useServiceRewards,
} from 'features/reward/state/service-rewards-context';
import { findMatchingRewardForCartEntry } from 'features/reward/utils/identity';
import { appendRewardProductIdentity } from 'features/reward/utils/routing';

import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import LoyaltyPointsIcon from 'components/icons/loyalty-points-icon';
import { LoyaltyIncentiveSwapDialog } from 'components/loyalty-incentive-swap-dialog';
import { Typography } from 'components/ui';
import { LoyaltyPromotionType } from 'generated/graphql-gateway';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import {
  getCartIdToEvictForNewRewardSlot,
  sumAppliedTimesApplied,
} from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { IncentiveEvaluationMap } from 'state/loyalty/hooks/types';
import { RewardRedemptionMethod } from 'state/loyalty/hooks/types';
import { useRewardsEvaluation } from 'state/loyalty/hooks/use-loyalty-rewards-evaluation';
import { useRewardRedemptionLimit } from 'state/loyalty/hooks/use-reward-redemption-limit';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { primitive, spacing } from 'styles/constants';
import { getParentIdFromUrl } from 'utils/cart';

export enum RewardActions {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
  NONE = 'NONE',
}

export interface RedeemRewardActionButtonProps {
  /** Type of reward action */
  action: RewardActions;
  /** Whether the button should be disabled or not */
  disabled?: boolean;
  /** Click handler for redeeming reward */
  onRewardAdd: () => void;
  /** Click handler for removing reward */
  onRewardRemove: () => void;
  /** Point value of current reward */
  points: number;
  /** If a locked state be displayed  */
  locked?: boolean;
  /** Loading flag  */
  loading?: boolean;
}

export const RewardActionButton = ({
  action,
  disabled,
  onRewardAdd,
  onRewardRemove,
  points,
  locked,
  loading,
}: RedeemRewardActionButtonProps) => {
  const isAdd = action === RewardActions.ADD;

  const buttonStyles = {
    backgroundColor: disabled
      ? primitive.$white
      : isAdd
      ? primitive.$meltyYellow
      : primitive.$white,
    textColor: disabled ? primitive.$blackOpacity50 : primitive.$bbqBrown,
    variant: disabled
      ? ActionButtonVariants.OUTLINE
      : isAdd
      ? ActionButtonVariants.PRIMARY
      : ActionButtonVariants.OUTLINE,
    borderColor: disabled ? primitive.$blackOpacity50 : isAdd ? '' : primitive.$bbqBrown,
  };

  const { backgroundColor, textColor, variant, borderColor } = buttonStyles;

  const redeemKey = isAdd ? 'redeemApply' : 'redeemRemove';
  const accessibilityLabel = `${t(redeemKey, { points })} ${t('crowns')} `;

  const handleAction = () => {
    if (disabled || loading) {
      return;
    }
    if (action === RewardActions.NONE) {
      return;
    }
    if (action === RewardActions.ADD) {
      onRewardAdd();
    }
    if (action === RewardActions.REMOVE) {
      onRewardRemove();
    }
  };

  return (
    <ActionButton
      variant={variant}
      size={ActionButtonSizes.SMALL}
      disabled={disabled}
      onPress={handleAction}
      testID={`redeem-${isAdd ? 'add' : 'remove'}`}
      background={backgroundColor}
      isLoading={loading}
      accessibilityLabel={accessibilityLabel}
      borderColor={borderColor}
      _hover={{ borderColor }}
      _pressed={{ borderColor }}
      keepIconSize
      leftIcon={
        isAdd || action === RewardActions.NONE ? undefined : (
          <Icon color={primitive.$black} variant="cancel" size="xs" />
        )
      }
      rightIcon={
        loading ? undefined : (
          <LoyaltyPointsIcon
            width="15px"
            height="15px"
            fill={disabled ? primitive.$blackOpacity50 : isAdd ? textColor : primitive.$meltyYellow}
          />
        )
      }
      _spinner={{
        height: spacing.$3_5,
        width: spacing.$3_5,
      }}
    >
      <Typography variant="headerOne" color={textColor} fontSize="xs" lineHeight="xs" m="$0">
        {locked ? points : isAdd ? t('redeemApply', { points }) : points}
      </Typography>
    </ActionButton>
  );
};

const getRewardAction = ({
  cartEntry,
  isRewardEligible,
}: {
  cartEntry: ICartEntry;
  isRewardEligible: boolean;
}) => {
  if (isRewardEligible) {
    return RewardActions.ADD;
  }
  if (cartEntry.children?.[0]?.type === MinimalCartEntryType.combo) {
    return RewardActions.NONE;
  }
  return RewardActions.REMOVE;
};

export const CartEntryRewardActionButton = ({
  cartEntry,
  isLoading,
}: {
  cartEntry: ICartEntry;
  isLoading: boolean;
}) => {
  useEnableServiceRewardsBrowse();
  const { enabled: serviceRewardsEnabled } = useEnableLoyaltyServiceRewards();
  const { rewardsMap } = useServiceRewards();
  const { indexedRewards } = useUnifiedMenuContext();
  const { replaceItemInMinimalCart, removeItemFromMinimalCart, cartEntries } = useMinimalCart();
  const { requestCartValidation } = useCartLiveValidationActions();
  const { rewardsEvaluationMap, isRewardsEvaluationLoading } = useRewardsEvaluation();
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const unavailableRewardsMap = useRef<IncentiveEvaluationMap>({});
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const rewardRedemptionLimit = useRewardRedemptionLimit();
  const appliedRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);

  const availableLoyaltyRewardsMap = useAppSelector(
    selectors.loyalty.selectAvailableLoyaltyRewardsMap
  );

  // When a reward is applied, the cart entry becomes `type: reward` and the reward eligibility
  // is tied to the underlying benefit item (the first child), not the reward entry id itself.
  // If we keep looking up rewards by `cartEntry.id`, the reward reference can go missing and the
  // button unmounts (appears to "disappear").
  const rewardBenefitEntry =
    cartEntry.type === MinimalCartEntryType.reward ? cartEntry.children?.[0] : undefined;
  const rewardLookupId = rewardBenefitEntry?.id ?? cartEntry.id;
  const rewardLookupUrl = rewardBenefitEntry?.url ?? cartEntry.url;
  const rewardLookupParentId = getParentIdFromUrl(rewardLookupUrl);

  const serviceRewardReference = serviceRewardsEnabled
    ? findMatchingRewardForCartEntry(Object.values(rewardsMap), {
        sanityId: rewardLookupId,
        type: rewardBenefitEntry?.type ?? cartEntry.type,
        url: rewardLookupUrl,
      })
    : undefined;
  const legacyRewardReference =
    availableLoyaltyRewardsMap[rewardLookupId] || availableLoyaltyRewardsMap[rewardLookupParentId];
  const rewardReference = serviceRewardsEnabled ? serviceRewardReference : legacyRewardReference;

  const reward =
    cartEntry.type === MinimalCartEntryType.reward ? indexedRewards.find(cartEntry.id) : undefined;

  useEffect(() => {
    if (Object.keys(rewardsEvaluationMap).length) {
      unavailableRewardsMap.current = rewardsEvaluationMap;
    }
  }, [rewardsEvaluationMap]);

  const activeRewardsEvaluationMap =
    isRewardsEvaluationLoading && !Object.keys(rewardsEvaluationMap).length
      ? unavailableRewardsMap.current
      : rewardsEvaluationMap;
  const isRewardUnavailable = serviceRewardsEnabled
    ? Boolean(serviceRewardReference?.errors?.length)
    : Boolean(
        rewardReference &&
          Object.keys(activeRewardsEvaluationMap).length &&
          activeRewardsEvaluationMap[rewardReference.id]
      );
  const shouldRenderRewardAction = Boolean(rewardReference && !isRewardUnavailable);

  // For non-reward cart entries, if there is no matching reward reference we should not render the button.
  // For reward entries, always render so the user can remove the applied reward.
  if (!shouldRenderRewardAction && cartEntry.type !== MinimalCartEntryType.reward) {
    return null;
  }

  const isRewardEligible = shouldRenderRewardAction && cartEntry.type === MinimalCartEntryType.item;
  const points = rewardReference?.pointCost ?? reward?.loyaltyPoints ?? 0;

  const onRewardRemove = (cartEntryToRemove: ICartEntry) => {
    // we need to remove the entry from the cart and add it back without the reward applied
    const benefitCardEntry = cartEntryToRemove.children[0];
    if (!benefitCardEntry) {
      // still remove the entry from the cart even if it doesn't have a benefit card entry
      removeItemFromMinimalCart(cartEntryToRemove);
      requestCartValidation();
      return;
    }
    const baseUrl = benefitCardEntry.url.split('?')[0];
    // TODO: remove this when we have a way to find the combo entry in the menu
    if (cartEntryToRemove.children[0].type === MinimalCartEntryType.combo) {
      removeItemFromMinimalCart(cartEntryToRemove);
      requestCartValidation();
      return;
    }
    replaceItemInMinimalCart(cartEntryToRemove, {
      cartId: cartEntryToRemove.cartId,
      ...benefitCardEntry,
      url: baseUrl,
    });
    requestCartValidation();
  };

  const onRewardAdd = () => {
    if (serviceRewardsEnabled && !serviceRewardReference?.rewardBenefitId) {
      return;
    }

    const newCartId = cartEntry.cartId ?? '';
    const evictCartId =
      enableRewardUnstacking &&
      getCartIdToEvictForNewRewardSlot(appliedRewards, newCartId, rewardRedemptionLimit);

    if (evictCartId) {
      const evicted = appliedRewards[evictCartId];
      const cartEntryToRemove = cartEntries.find(
        entry =>
          entry.cartId === evictCartId || (evicted?.sanityId && entry.id === evicted.sanityId)
      );
      if (cartEntryToRemove) {
        onRewardRemove(cartEntryToRemove);
      }
    }

    // we need to remove the entry from the cart and add it back with the reward applied
    // Preserve the child entry with the same cartId and lineId for stable rendering
    const childEntry: ICartEntry = {
      ...cartEntry,
      cartId: cartEntry.cartId,
    };
    const rewardDocumentId = serviceRewardsEnabled
      ? serviceRewardReference?.cmsId ?? serviceRewardReference?.id
      : legacyRewardReference?.sanityId;

    const rewardCartEntry: ICartEntry = {
      id: rewardDocumentId ?? '',
      rewardId: rewardDocumentId ?? undefined,
      cartId: cartEntry.cartId,
      ...(serviceRewardsEnabled && rewardReference?.id ? { referenceId: rewardReference.id } : {}),
      price: {
        amount: (cartEntry.price?.amount ?? 0) * -1,
        currency: 'USD',
      },
      name: cartEntry.name,
      image: cartEntry.image,
      quantity: cartEntry.quantity,
      menuSelections: [
        {
          id: cartEntry.id,
          quantity: cartEntry.quantity,
          menuSelections: cartEntry.menuSelections,
        },
      ],
      children: [childEntry],
      type: MinimalCartEntryType.reward,
      url:
        serviceRewardsEnabled && serviceRewardReference
          ? appendRewardProductIdentity({
              pathname: cartEntry.url.split('?')[0],
              reward: {
                id: serviceRewardReference.id,
                cmsId: serviceRewardReference.cmsId,
                rewardBenefitId: serviceRewardReference.rewardBenefitId,
              },
            })
          : `${cartEntry.url}?rewardId=${rewardDocumentId}`,
    };
    replaceItemInMinimalCart(cartEntry, rewardCartEntry);
    requestCartValidation();
    const baseApplication = {
      cartId: cartEntry.cartId ?? '',
      redemptionMethod: RewardRedemptionMethod.MOBILE,
      enableRewardUnstacking,
      rewardRedemptionLimit,
    };
    dispatch(
      actions.loyalty.applyReward(
        serviceRewardsEnabled && serviceRewardReference
          ? {
              ...baseApplication,
              reward: {
                id: serviceRewardReference.id,
                cmsId: serviceRewardReference.cmsId,
                rewardBenefitId: serviceRewardReference.rewardBenefitId!,
                pointCost: serviceRewardReference.pointCost,
                limitPerOrder: serviceRewardReference.limitPerOrder,
              },
            }
          : {
              ...baseApplication,
              rewardBenefitId: cartEntry.id ?? '',
            }
      )
    );
  };

  return (
    <>
      <LoyaltyIncentiveSwapDialog
        incentiveType={LoyaltyPromotionType.REWARD}
        maxRewards={rewardRedemptionLimit}
        appliedRewards={sumAppliedTimesApplied(appliedRewards)}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onSwap={() => {
          onRewardAdd();
          setIsOpen(false);
        }}
      />
      <RewardActionButton
        action={getRewardAction({ cartEntry, isRewardEligible })}
        disabled={isLoading}
        onRewardAdd={() => {
          const atCapacity =
            enableRewardUnstacking &&
            sumAppliedTimesApplied(appliedRewards) >= rewardRedemptionLimit;
          if (atCapacity) {
            setIsOpen(true);
          } else {
            onRewardAdd();
          }
        }}
        onRewardRemove={() => onRewardRemove(cartEntry)}
        points={points}
      />
    </>
  );
};
